/**
 * PropertyBasics Step Component
 *
 * Step 1 of listing wizard: property type, room type, basic details.
 */

import React from 'react';
import { ListingFormData, RoomType } from '../../hooks';
import { useTaxonomies } from '../../hooks';

interface PropertyBasicsProps {
  data: ListingFormData;
  onChange: (updates: Partial<ListingFormData>) => void;
}

const roomTypes: { value: RoomType; label: string; description: string }[] = [
  {
    value: 'entire_place',
    label: 'Entire place',
    description: 'Guests have the whole place to themselves',
  },
  {
    value: 'private_room',
    label: 'Private room',
    description: 'Guests have their own room in a shared space',
  },
  {
    value: 'shared_room',
    label: 'Shared room',
    description: 'Guests sleep in a room shared with others',
  },
];

export const PropertyBasics: React.FC<PropertyBasicsProps> = ({ data, onChange }) => {
  const { data: taxonomies, isLoading } = useTaxonomies();

  return (
    <div className="space-y-8">
      {/* Property Type */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          What type of property is this?
        </h3>
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {taxonomies?.propertyTypes.map((type) => (
              <button
                key={type.slug}
                type="button"
                onClick={() => onChange({ propertyType: type.slug })}
                className={`p-4 rounded-xl border-2 text-left transition-colors ${
                  data.propertyType === type.slug
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <PropertyIcon type={type.slug} className="w-6 h-6 text-gray-600 mb-2" />
                <span className="font-medium text-gray-900">{type.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Room Type */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          What type of space will guests have?
        </h3>
        <div className="space-y-3">
          {roomTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => onChange({ roomType: type.value })}
              className={`w-full p-4 rounded-xl border-2 text-left transition-colors ${
                data.roomType === type.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="font-medium text-gray-900">{type.label}</span>
              <p className="text-sm text-gray-500 mt-1">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Create a title for your listing
        </h3>
        <input
          type="text"
          value={data.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="e.g., Cozy Mountain Chalet with Amazing Views"
          maxLength={100}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <p className="text-sm text-gray-500 mt-2">
          {data.title.length}/100 characters
        </p>
      </div>

      {/* Description */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Describe your property
        </h3>
        <textarea
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Tell guests what makes your place special..."
          rows={6}
          maxLength={2000}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
        />
        <p className="text-sm text-gray-500 mt-2">
          {data.description.length}/2000 characters
        </p>
      </div>

      {/* Capacity */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Property details
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CounterInput
            label="Bedrooms"
            value={data.bedrooms}
            min={0}
            max={20}
            onChange={(v) => onChange({ bedrooms: v })}
          />
          <CounterInput
            label="Beds"
            value={data.beds}
            min={1}
            max={50}
            onChange={(v) => onChange({ beds: v })}
          />
          <CounterInput
            label="Bathrooms"
            value={data.bathrooms}
            min={1}
            max={20}
            onChange={(v) => onChange({ bathrooms: v })}
          />
          <CounterInput
            label="Max guests"
            value={data.maxGuests}
            min={1}
            max={50}
            onChange={(v) => onChange({ maxGuests: v })}
          />
        </div>
      </div>
    </div>
  );
};

// Counter Input Component
interface CounterInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

const CounterInput: React.FC<CounterInputProps> = ({ label, value, min, max, onChange }) => (
  <div className="bg-gray-50 rounded-xl p-4">
    <label className="block text-sm text-gray-600 mb-2">{label}</label>
    <div className="flex items-center justify-between">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <MinusIcon className="w-4 h-4" />
      </button>
      <span className="font-semibold text-gray-900">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// Property Icon Component
const PropertyIcon: React.FC<{ type: string; className?: string }> = ({ type, className }) => {
  const icons: Record<string, React.ReactNode> = {
    chalet: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    apartment: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    villa: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
      </svg>
    ),
  };

  return (
    icons[type] || (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    )
  );
};

// Icons
const MinusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

export default PropertyBasics;
